import "./FrameImagePicker.css";
import { Row, Col, Button, Modal, Space, Input, message } from "antd";
import { ExpandOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { AttachSignedUrls } from "../../../../utils/GetSignedURL";

export default function FrameImagePicker({
  images = [],                 
  sessionId,
  title = "Select Image",
  value,                      
  onChange,                    
  showRevise = false,         
  onRevise,                 
  signingConcurrency = 1,
}) {
  const imgBoxRef = useRef(null);
  const [messageApi, contextHolder] = message.useMessage();

  const [open, setOpen] = useState(false);
  const [reviseText, setReviseText] = useState("");

  const [signedImages, setSignedImages] = useState([]);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!sessionId || !(images ?? []).length) {
        if (!cancelled) {
          setSignedImages([]);
          setSigning(false);
        }
        return;
      }

      try {
        setSigning(true);

        const needsSign = (images ?? []).some(
          (x) => (x?.imagePath || x?.gcs_uri || x?.uri) && !x?.signed_url
        );

        if (!needsSign) {
          if (!cancelled) setSignedImages(images ?? []);
          return;
        }

        const withUrl = await AttachSignedUrls(images ?? [], signingConcurrency);

        if (!cancelled) setSignedImages(withUrl ?? []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setSignedImages([]);
        messageApi.error(e?.message ?? "Failed to sign image URLs");
      } finally {
        if (!cancelled) setSigning(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [sessionId, images, signingConcurrency, messageApi]);

  const pickUrl = (v) => {
    if (!v) return "";
    if (typeof v === "string") return v;
    if (typeof v === "object") return v.signed_url || v.signed_uri || "";
    return "";
  };

  const items = useMemo(() => {
    return (signedImages ?? []).map((it, idx) => {
      const rawUri =
        it?.gcs_uri ||
        it?.imagePath ||
        it?.uri ||
        (typeof it?.signed_url === "object" ? it?.signed_url?.gcs_uri : "") ||
        "";

      return {
        key: rawUri || String(idx),
        displayUrl: pickUrl(it?.signed_url) || pickUrl(it?.signed_uri) || pickUrl(it?.url),
        rawUri,
        raw: it,
      };
    });
  }, [signedImages]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const current = items.find((x) => x.key === value) || items[0];
  const currentUrl = current?.displayUrl || "";

  return (
    <div>
      {contextHolder}

      <Space style={{ marginBottom: 12, opacity: 0.8 }}>
        <span>{title}</span>
        {signing && <span>Loading image…</span>}
      </Space>

      <Row gutter={[12, 12]}>
        {(items ?? []).map((it) => (
          <Col xs={12} md={8} key={it.key}>
            <div
              className={`fp-tile ${it.key === value ? "is-selected" : ""}`}
              onClick={() => onChange?.(it.key, it)}
            >
              {it.displayUrl ? (
                <img
                  className="fp-img"
                  src={it.displayUrl}
                  alt="frame"
                />
              ) : (
                <div className="fp-empty">No URL</div>
              )}


              <div className="fp-overlay" onClick={(e) => e.stopPropagation()}>
                <Button
                  type="text"
                  icon={<ExpandOutlined />}
                  onClick={() => {
                    if (!it.displayUrl) return;
                    onChange?.(it.key, it);
                    setOpen(true);
                  }}
                  disabled={!it.displayUrl}
                />
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        centered
        width="90vw"
        styles={{ body: { padding: 0 } }}
      >
        <div ref={imgBoxRef} style={{ width: "100%" }}>
          <img className="fp-imgLarge" src={currentUrl} alt="preview" />
        </div>
      </Modal>

      {showRevise && (
        <div className="fp-reviseRow">
          <Input.TextArea
            value={reviseText}
            onChange={(e) => setReviseText(e.target.value)}
            placeholder="Revise with instructions"
            autoSize={{ minRows: 1, maxRows: 3 }}
          />
          <Button
            type="primary"
            disabled={!reviseText.trim() || !current}
            onClick={() => onRevise?.(reviseText, current?.raw)}
          >
            Revise
          </Button>
        </div>
      )}
    </div>
  );
}
